import { deviceStorage, type HapticApi } from "@/lib/telegram";
import { ACTIONS, FEATURES, track } from "@/lib/analytics";
import type { AppearanceApi } from "@/lib/theme";
import type { BackButtonApi, MainButtonApi, Platform, SharePayload } from "./types";
import { useIosUiStore } from "./iosUiStore";
import { useToastStore } from "./toastStore";

const noop = (): void => {};

/** DUE-318 §2: the browser has no haptics, and `HapticApi` is call-and-forget. */
const webHaptics: HapticApi = { impact: noop, notification: noop, selectionChanged: noop };

/* The DOM chrome iOS already draws (`IosBackControl`, `IosMainButtonBar`,
 * gated `name !== "telegram"`) is the web's chrome too. Escape mirrors the
 * back tap so a desktop keyboard can leave a screen. */
const backButton: BackButtonApi = {
  show(onClick) {
    const hide = useIosUiStore.getState().showBack(onClick);
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClick();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      hide();
    };
  },
};

const mainButton: MainButtonApi = {
  set(state) {
    return useIosUiStore.getState().setMain(state);
  },
};

/* `data-theme` on <html> is the whole story in a browser. */
const appearance: AppearanceApi = { apply: noop };

async function fileFromImage(imageUrl: string): Promise<File | null> {
  try {
    const blob = await fetch(imageUrl).then((r) => (r.ok ? r.blob() : null));
    return blob ? new File([blob], "psyna.png", { type: blob.type || "image/png" }) : null;
  } catch {
    return null;
  }
}

async function share(payload: SharePayload): Promise<void> {
  const nav = navigator as Navigator & {
    share?: (data: ShareData) => Promise<void>;
    canShare?: (data: ShareData) => boolean;
  };
  if (typeof nav.share === "function") {
    const data: ShareData = { text: payload.text, url: payload.url };
    if (payload.imageUrl && typeof nav.canShare === "function") {
      const file = await fileFromImage(payload.imageUrl);
      if (file && nav.canShare({ files: [file] })) data.files = [file];
    }
    try {
      await nav.share(data);
      track(FEATURES.web, ACTIONS.shareMethod, { method: "native" });
      return;
    } catch (error) {
      // AbortError = the user closed the sheet without picking a target —
      // that is a dismissal, not a share, so stop here: no clipboard copy,
      // no event. Anything else (no share target available, etc.) falls
      // back to the clipboard below.
      if (error && typeof error === "object" && "name" in error && error.name === "AbortError") {
        return;
      }
    }
  }
  const link = payload.url ?? payload.imageUrl;
  if (!link) return;
  try {
    await navigator.clipboard.writeText(link);
    useToastStore.getState().show("share.link_copied");
    track(FEATURES.web, ACTIONS.shareMethod, { method: "clipboard" });
  } catch {
    // `navigator.clipboard` is `undefined` on an insecure origin (plain
    // http, some in-app webviews) and `writeText` can reject (denied
    // permission) — either way the user gets nothing with no trace of it.
    // No toast (there is nothing to confirm), but the hole is at least
    // visible in the metric.
    track(FEATURES.web, ACTIONS.shareMethod, { method: "unavailable" });
  }
}

export interface CreateWebPlatformDeps {
  /** Test seam for close(); defaults to a real navigation. */
  navigateHome?: () => void;
}

/**
 * DUE-318 §2: the browser is the third surface. Storage and refresh tokens
 * are `localStorage` (`deviceStorage`) — the floor `admin-web` already
 * accepts; there is no OS keystore to do better.
 */
export function createWebPlatform(deps: CreateWebPlatformDeps = {}): Platform {
  const navigateHome = deps.navigateHome ?? ((): void => window.location.assign("/"));
  return {
    name: "web",
    haptics: webHaptics,
    storage: deviceStorage,
    secureStorage: deviceStorage,
    backButton,
    mainButton,
    appearance,
    share,
    openLink(url: string): void {
      window.open(url, "_blank", "noopener");
    },
    close(): void {
      navigateHome();
    },
    supportsPurchases: false,
    purchases: null,
  };
}
